/**
 * Getting around: page titles, "home" for the current person, and a Back
 * that returns to the previous page in the app, or to a sensible page when
 * there isn't one (opened from an email link, a QR poster or a bookmark).
 * The iPhone home-screen app has no browser back button, so every page
 * that isn't a main destination needs this.
 */
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useSession } from './session.ts';

const APP_NAME = 'IT Helpdesk';

/** "Report a problem · IT Helpdesk" in the tab, history and app switcher. */
export function usePageTitle(title?: string | null) {
  useEffect(() => {
    document.title = title ? `${title} · ${APP_NAME}` : `NABOCADO ${APP_NAME}`;
  }, [title]);
}

/** Where this person's work starts: the queue for IT, their tickets for staff, else home. */
export function useHomePath() {
  const { user, isIt } = useSession();
  return user ? (isIt ? '/queue' : '/tickets') : '/';
}

/**
 * Returns a function that goes back one page when the previous page was in
 * this app, or to `fallback` when this is the first page of the visit.
 */
export function useGoBack(fallback: string) {
  const navigate = useNavigate();
  const location = useLocation();
  // React Router gives the page a visit started on the key "default".
  const hasPrevious = location.key !== 'default';
  return () => {
    if (hasPrevious) navigate(-1);
    else navigate(fallback, { replace: true });
  };
}
